/**
 * useForm Custom Hook
 * Handles form state, validation, and submission
 * Designed to be easily extended for API integration
 */

import { useState, useCallback } from 'react';

/**
 * Validation rules that can be applied to form fields
 */
const validators = {
    required: (value) => {
        if (Array.isArray(value)) {
            return value.length > 0 ? '' : 'This field is required';
        }
        return value && value.trim() !== '' ? '' : 'This field is required';
    },
    email: (value) => {
        if (!value) return '';
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(value) ? '' : 'Please enter a valid email address';
    },
    phone: (value) => {
        if (!value) return '';
        const phoneRegex = /^[+]?[\d\s-()]{8,}$/;
        return phoneRegex.test(value) ? '' : 'Please enter a valid phone number';
    },
    minLength: (min) => (value) => {
        if (!value) return '';
        return value.length >= min ? '' : `Must be at least ${min} characters`;
    },
};

/**
 * Custom hook for form handling
 * @param {Object} initialValues - Initial form values
 * @param {Object} validationRules - Validation rules per field
 * @param {Function} onSubmit - Async function to call on valid submission
 */
function useForm(initialValues, validationRules = {}, onSubmit) {
    // Form values state
    const [values, setValues] = useState(initialValues);
    // Form errors state
    const [errors, setErrors] = useState({});
    // Touched fields state (for showing errors only after interaction)
    const [touched, setTouched] = useState({});
    // Submission state
    const [isSubmitting, setIsSubmitting] = useState(false);
    // Success state
    const [isSuccess, setIsSuccess] = useState(false);
    // Error message for submission failure
    const [submitError, setSubmitError] = useState(null);

    /**
     * Validate a single field
     */
    const validateField = useCallback((name, value) => {
        const rules = validationRules[name];
        if (!rules) return '';

        for (const rule of rules) {
            let errorMessage = '';

            if (typeof rule === 'string') {
                // Built-in validator
                if (validators[rule]) {
                    errorMessage = validators[rule](value);
                }
            } else if (typeof rule === 'function') {
                // Custom validator function
                errorMessage = rule(value);
            } else if (rule.type && validators[rule.type]) {
                // Parameterized validator
                errorMessage = validators[rule.type](rule.value)(value);
            }

            if (errorMessage) {
                return errorMessage;
            }
        }

        return '';
    }, [validationRules]);

    /**
     * Validate all fields
     */
    const validateAll = useCallback(() => {
        const newErrors = {};
        let isValid = true;

        Object.keys(validationRules).forEach((name) => {
            const error = validateField(name, values[name]);
            if (error) {
                newErrors[name] = error;
                isValid = false;
            }
        });

        setErrors(newErrors);
        return isValid;
    }, [values, validationRules, validateField]);

    /**
     * Handle input change
     */
    const handleChange = useCallback((event) => {
        const { name, value, type, checked } = event.target;
        const newValue = type === 'checkbox' ? checked : value;

        setValues((prev) => ({ ...prev, [name]: newValue }));

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: '' }));
        }

        // Clear success state when form is modified
        if (isSuccess) {
            setIsSuccess(false);
        }
    }, [errors, isSuccess]);

    /**
     * Handle select change for multi-select
     */
    const handleMultiSelect = useCallback((name, selectedValues) => {
        setValues((prev) => ({ ...prev, [name]: selectedValues }));

        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: '' }));
        }
    }, [errors]);

    /**
     * Handle input blur (for validation on blur)
     */
    const handleBlur = useCallback((event) => {
        const { name, value } = event.target;

        setTouched((prev) => ({ ...prev, [name]: true }));

        const error = validateField(name, value);
        setErrors((prev) => ({ ...prev, [name]: error }));
    }, [validateField]);

    /**
     * Handle form submission
     */
    const handleSubmit = useCallback(async (event) => {
        if (event) {
            event.preventDefault();
        }

        // Mark all fields as touched
        const allTouched = Object.keys(values).reduce((acc, key) => {
            acc[key] = true;
            return acc;
        }, {});
        setTouched(allTouched);

        // Validate all fields
        const isValid = validateAll();

        if (!isValid) {
            return;
        }

        setIsSubmitting(true);
        setSubmitError(null);

        try {
            // Call the onSubmit handler
            // This is where API integration would happen
            if (onSubmit) {
                await onSubmit(values);
            } else {
                // Simulate API call for demo
                await new Promise((resolve) => setTimeout(resolve, 1000));
            }

            setIsSuccess(true);
            // Reset form after successful submission
            setValues(initialValues);
            setTouched({});
            setErrors({});
        } catch (error) {
            setSubmitError(error.message || 'Something went wrong. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    }, [values, validateAll, onSubmit, initialValues]);

    /**
     * Reset form to initial state
     */
    const resetForm = useCallback(() => {
        setValues(initialValues);
        setErrors({});
        setTouched({});
        setIsSubmitting(false);
        setIsSuccess(false);
        setSubmitError(null);
    }, [initialValues]);

    /**
     * Get field props helper
     */
    const getFieldProps = useCallback((name) => ({
        name,
        value: values[name] || '',
        onChange: handleChange,
        onBlur: handleBlur,
        'aria-invalid': touched[name] && errors[name] ? 'true' : 'false',
    }), [values, handleChange, handleBlur, touched, errors]);

    /**
     * Get field error (only show if touched)
     */
    const getFieldError = useCallback((name) => {
        return touched[name] ? errors[name] : '';
    }, [touched, errors]);

    return {
        values,
        errors,
        touched,
        isSubmitting,
        isSuccess,
        submitError,
        handleChange,
        handleBlur,
        handleSubmit,
        handleMultiSelect,
        resetForm,
        getFieldProps,
        getFieldError,
        setValues,
    };
}

export default useForm;
